//============================================================================
// Name        : universal_permanent_status_registry.ts
// Purpose     : Universal Permanent Status Registry page interactions
// Author      : Senitron Team
// Copyright   : Copyright 2026 Senitron Corporation. All rights reserved.
//============================================================================

import { InitPageStrict } from "../index";

type ToastKind = "success" | "warning" | "error";

const TOAST_ICONS: Record<ToastKind, string> = {
  success: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>',
  warning: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
  error: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>'
};

class UniversalPermanentStatusRegistry {
  private csrfToken: string;
  private toastTimer: number | null = null;

  constructor() {
    this.csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "";
    this.bindModalTriggers();
    this.bindModalClosers();
    this.bindSelectAll();
    this.bindImportFilename();
    this.bindImportDropzone();
    this.bindDelete();
    this.bindClearAll();
    this.bindDetails();
    this.bindFilterDropdowns();
    this.bindRequiredStatus();
    this.bindSubmitGuards();
    this.showFlashToast();
  }

  private bindRequiredStatus(): void {
    const forms = [
      { form: "upsr-add-form", dropdown: "addStatusCats", fileInputId: null },
      { form: "upsr-import-form", dropdown: "importStatusCats", fileInputId: "upsr-import-file" }
    ];

    forms.forEach(({ form, dropdown, fileInputId }) => {
      const formEl = document.getElementById(form) as HTMLFormElement;
      const dropdownEl = document.getElementById(dropdown);
      if (!formEl || !dropdownEl) return;

      const value = dropdownEl.querySelector<HTMLInputElement>(".pick-cats-value");

      formEl.addEventListener("submit", (e: Event) => {
        if (fileInputId) {
          const fileInput = document.getElementById(fileInputId) as HTMLInputElement;
          if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
            e.preventDefault();
            this.showToast("Please choose a file to import.", "error");
            return;
          }
        }

        if (value && !value.value) {
          e.preventDefault();
          dropdownEl.classList.add("pick-cats--invalid");
          this.showToast("Please select a status.", "error");
        }
      });

      dropdownEl.addEventListener("click", () => dropdownEl.classList.remove("pick-cats--invalid"));
    });
  }

  private disableButton(button: HTMLElement | null, label?: string): void {
    if (!button) return;
    button.setAttribute("disabled", "disabled");
    button.classList.add("disabled");
    button.style.pointerEvents = "none";
    if (label) button.textContent = label;
  }

  private enableButton(button: HTMLElement | null, label?: string): void {
    if (!button) return;
    button.removeAttribute("disabled");
    button.classList.remove("disabled");
    button.style.pointerEvents = "";
    if (label) button.textContent = label;
  }

  private bindSubmitGuards(): void {
    const forms = [
      { form: "upsr-add-form", button: "add-btn", label: "Adding…" },
      { form: "upsr-import-form", button: "import-btn", label: "Importing…" }
    ];

    forms.forEach(({ form, button, label }) => {
      const formEl = document.getElementById(form) as HTMLFormElement;
      if (!formEl) return;

      formEl.addEventListener("submit", (e: Event) => {
        if (e.defaultPrevented) return;
        this.disableButton(document.getElementById(button), label);
      });
    });

    // Export downloads a file, so the page never navigates away: re-enable shortly after.
    const exportBtn = document.getElementById("exportConfirmBtn");
    if (exportBtn) {
      const originalLabel = exportBtn.textContent || "";
      exportBtn.addEventListener("click", () => {
        this.disableButton(exportBtn, "Exporting…");
        window.setTimeout(() => this.enableButton(exportBtn, originalLabel), 4000);
      });
    }
  }

  private showFlashToast(): void {
    const flash = document.getElementById("upsr-flash");
    if (!flash) return;

    if (flash.dataset.notice) this.showToast(flash.dataset.notice, "success");
    else if (flash.dataset.warning) this.showToast(flash.dataset.warning, "warning");
    else if (flash.dataset.alert) this.showToast(flash.dataset.alert, "error");
  }

  private showToast(message: string, kind: ToastKind = "success"): void {
    const toast = document.getElementById("toast");
    if (!toast || !message) return;

    toast.innerHTML = "";
    toast.classList.toggle("toast--success", kind === "success");
    toast.classList.toggle("toast--warning", kind === "warning");
    toast.classList.toggle("toast--error", kind === "error");
    toast.insertAdjacentHTML("beforeend", TOAST_ICONS[kind]);
    const span = document.createElement("span");
    span.textContent = message;
    toast.appendChild(span);

    toast.classList.add("show");
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => toast.classList.remove("show"), 4000);
  }

  private bindFilterDropdowns(): void {
    document.querySelectorAll<HTMLElement>(".pick-cats").forEach((dropdown) => {
      const trigger = dropdown.querySelector<HTMLElement>(".pick-cats-trigger");
      const panel = dropdown.querySelector<HTMLElement>(".pick-cats-panel");
      const search = dropdown.querySelector<HTMLInputElement>(".pick-cats-search-input");
      const label = dropdown.querySelector<HTMLElement>(".pick-cats-label");
      const value = dropdown.querySelector<HTMLInputElement>(".pick-cats-value");
      const empty = dropdown.querySelector<HTMLElement>(".pick-cats-empty");
      const rows = Array.from(dropdown.querySelectorAll<HTMLElement>(".pick-cats-row"));
      const form = dropdown.closest("form") as HTMLFormElement;
      const defaultLabel = dropdown.dataset.defaultLabel || "";
      if (!trigger || !panel || !value) return;

      const open = () => {
        panel.style.display = "block";
        dropdown.classList.add("open");
        if (search) { search.value = ""; this.filterRows(rows, empty, ""); search.focus(); }
      };
      const close = () => {
        panel.style.display = "none";
        dropdown.classList.remove("open");
      };

      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        dropdown.classList.contains("open") ? close() : open();
      });
      panel.addEventListener("click", (e) => e.stopPropagation());
      if (search) search.addEventListener("input", () => this.filterRows(rows, empty, search.value));

      const applyBtn = dropdown.querySelector(".pick-cats-apply");

      rows.forEach((row) => {
        row.addEventListener("click", () => {
          rows.forEach((r) => r.classList.remove("selected"));
          row.classList.add("selected");
          value.value = row.dataset.value || "";
          if (label) label.textContent = row.querySelector("span")?.textContent || defaultLabel;
          if (!applyBtn) close();
        });
      });

      dropdown.querySelector(".pick-cats-apply")?.addEventListener("click", () => form?.submit());
      dropdown.querySelector(".pick-cats-clear")?.addEventListener("click", () => {
        rows.forEach((r) => r.classList.remove("selected"));
        value.value = "";
        if (label) label.textContent = defaultLabel;
        form?.submit();
      });

      document.addEventListener("click", (e: any) => {
        if (dropdown.classList.contains("open") && !dropdown.contains(e.target)) close();
      });
    });
  }

  private filterRows(rows: HTMLElement[], empty: HTMLElement | null, term: string): void {
    term = (term || "").toLowerCase();
    let anyVisible = false;
    rows.forEach((row) => {
      const match = (row.dataset.name || "").indexOf(term) !== -1;
      row.classList.toggle("hidden", !match);
      if (match) anyVisible = true;
    });
    if (empty) empty.style.display = anyVisible ? "none" : "block";
  }

  private openModal(id: string): void {
    const modal = document.getElementById(id);
    if (modal) modal.classList.add("open");
  }

  private closeModal(modal: HTMLElement): void {
    modal.classList.remove("open");
  }

  private on(id: string, handler: () => void): void {
    const el = document.getElementById(id);
    if (el) el.addEventListener("click", handler);
  }

  private bindModalTriggers(): void {
    this.on("addBtn", () => this.openModal("add-modal"));
    this.on("addBtnBottom", () => this.openModal("add-modal"));
    this.on("importBtn", () => this.openModal("import-modal"));
    this.on("importBtnBottom", () => this.openModal("import-modal"));
    this.on("exportBtn", () => this.openModal("export-modal"));
    this.on("clearBtn", () => this.openModal("clear-modal"));
  }

  private bindModalClosers(): void {
    document.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => {
      backdrop.addEventListener("click", (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target === backdrop || target.closest("[data-close]")) {
          this.closeModal(backdrop);
        }
      });
    });
  }

  private bindSelectAll(): void {
    const selectAll = document.getElementById("selectAllRegistries") as HTMLInputElement;
    if (!selectAll) return;

    const checkboxes = document.querySelectorAll<HTMLInputElement>(".registry-check");

    // Select / unselect all
    selectAll.addEventListener("change", () => {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = selectAll.checked;
      });
    });

    // Update "Select All" when individual checkbox changes
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        selectAll.checked = Array.from(checkboxes).every(
          (checkbox) => checkbox.checked
        );
      });
    });
  }



  private bindImportDropzone(): void {
    const zone = document.getElementById("file-dropzone");
    const input = document.getElementById("upsr-import-file") as HTMLInputElement;
    const modal = document.getElementById("import-modal");
    if (!zone || !input) return;

    // Clicking anywhere in the box (except the label/button) opens the file picker
    zone.addEventListener("click", (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("label, button, a")) return;
      input.click();
    });

    const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); };

    // Don't let the browser open a file dropped anywhere inside the modal
    if (modal) {
      ["dragover", "drop"].forEach((type) => modal.addEventListener(type, stop));
    }

    ["dragenter", "dragover"].forEach((type) =>
      zone.addEventListener(type, (e: Event) => {
        stop(e);
        zone.classList.add("dropzone-box--active");
      })
    );

    ["dragleave", "dragend", "drop"].forEach((type) =>
      zone.addEventListener(type, () => zone.classList.remove("dropzone-box--active"))
    );

    zone.addEventListener("drop", (e: DragEvent) => {
      stop(e);
      const dropped = e.dataTransfer?.files;
      if (!dropped || dropped.length === 0) return;

      const file = dropped[0];
      if (!/\.(csv|txt)$/i.test(file.name)) {
        this.showToast("Only CSV or TXT files are supported.", "error");
        return;
      }

      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  private bindImportFilename(): void {
    const input = document.getElementById("upsr-import-file") as HTMLInputElement;
    const label = document.getElementById("upsr-import-filename");
    if (!input || !label) return;

    input.addEventListener("change", () => {
      if (input.files && input.files.length > 0) {
        label.textContent = input.files[0].name;
        label.style.display = "inline-block";
      } else {
        label.style.display = "none";
      }
    });
  }

  private bindDelete(): void {
    document.querySelectorAll<HTMLElement>(".delete-registry").forEach((btn) => {
      btn.addEventListener("click", () => {
        const epcEl = document.getElementById("delete-epc");
        if (epcEl) epcEl.textContent = btn.dataset.epc || "";
        const confirmBtn = document.getElementById("delete-confirm-btn") as HTMLElement;
        if (confirmBtn) confirmBtn.dataset.id = btn.dataset.id || "";
        this.openModal("delete-modal");
      });
    });

    const confirmBtn = document.getElementById("delete-confirm-btn") as HTMLButtonElement;
    if (!confirmBtn) return;

    confirmBtn.addEventListener("click", () => {
      const modal = document.getElementById("delete-modal");
      const id = confirmBtn.dataset.id;
      const baseUrl = modal?.dataset.destroyUrl;
      if (!id || !baseUrl) return;

      this.disableButton(confirmBtn, "Removing…");

      const form = document.createElement("form");
      form.method = "post";
      form.action = `${baseUrl}/${id}`;
      form.innerHTML =
        `<input type="hidden" name="_method" value="delete">` +
        `<input type="hidden" name="authenticity_token" value="${this.csrfToken}">`;
      document.body.appendChild(form);
      form.submit();
    });
  }

  private bindClearAll(): void {
    const confirmBtn = document.getElementById("clear-confirm-btn") as HTMLButtonElement;
    const modal = document.getElementById("clear-modal");
    if (!confirmBtn || !modal) return;

    confirmBtn.addEventListener("click", () => {
      const url = modal.dataset.clearUrl;
      if (!url) return;

      this.disableButton(confirmBtn, "Clearing…");

      const form = document.createElement("form");
      form.method = "post";
      form.action = url;
      form.innerHTML =
        `<input type="hidden" name="_method" value="delete">` +
        `<input type="hidden" name="authenticity_token" value="${this.csrfToken}">`;
      document.body.appendChild(form);
      form.submit();
    });
  }

  private bindDetails(): void {
    const modal = document.getElementById("details-modal");
    if (!modal) return;
    const urlTemplate = modal.dataset.logUrl || "";

    document.querySelectorAll<HTMLElement>(".details-log").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.id;
        if (!id) return;
        this.loadDetails(urlTemplate.replace("LOG_ID", id));
      });
    });
  }

  private loadDetails(url: string): void {
    const modal = document.getElementById("details-modal") as HTMLElement;

    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((r) => r.json())
      .then((data) => {
        ["event", "datetime", "user", "source"].forEach((key) => {
          const el = modal.querySelector<HTMLElement>(`[data-detail="${key}"]`);
          if (el) el.textContent = data[key] || "—";
        });

        const referenceEl = modal.querySelector<HTMLElement>('[data-detail="reference"]');
        if (referenceEl) {
          referenceEl.innerHTML = "";
          const text = data.reference || "—";
          if (data.reference && data.reference_url) {
            const link = document.createElement("a");
            link.href = data.reference_url;
            link.target = "_blank";
            link.textContent = text;
            referenceEl.appendChild(link);
          } else {
            referenceEl.textContent = text;
          }
        }

        const statusEl = modal.querySelector<HTMLElement>('[data-detail="status"]');
        if (statusEl) {
          statusEl.innerHTML = "";
          if (data.status) {
            const pill = document.createElement("span");
            pill.className = "dl-status-pill";
            pill.textContent = data.status;
            statusEl.appendChild(pill);
          } else {
            statusEl.textContent = "—";
          }
        }

        const tagsWrap = document.getElementById("details-tags-wrap");
        const tagsBody = document.getElementById("details-tags");
        if (tagsBody) tagsBody.innerHTML = "";

        const tags = Array.isArray(data.tags) ? data.tags : [];
        if (tags.length > 0 && tagsBody && tagsWrap) {
          tags.forEach((tag: any) => {
            const row = document.createElement("tr");
            row.innerHTML = `<td>${tag.epc || ""}</td><td>${tag.serial || ""}</td>`;
            tagsBody.appendChild(row);
          });
          tagsWrap.style.display = "block";
        } else if (tagsWrap) {
          tagsWrap.style.display = "none";
        }

        this.openModal("details-modal");
      });
  }
}

export function LoadPage(): void {
  InitPageStrict("universal_permanent_status_registries index", () => {
    new UniversalPermanentStatusRegistry();
  });
}
